import styled from 'styled-components';

export const PageHeader = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  .eyebrow {
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.goldDark};
    margin-bottom: 0.5rem;
  }

  h1 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: clamp(1.75rem, 3vw, 2.3rem);
    color: ${({ theme }) => theme.colors.nearBlack};
    margin: 0;
  }

  p {
    color: ${({ theme }) => theme.colors.textMuted};
    margin-top: 0.4rem;
  }
`;

export const Panel = styled.section`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 2rem;
  margin-bottom: 1.75rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.colors.gold};
  }

  h2 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1.3rem;
    color: ${({ theme }) => theme.colors.nearBlack};
    margin-bottom: 0.35rem;
  }

  .subtitle {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.textMuted};
    margin-bottom: 1.25rem;
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => ($cols === 2 ? 'repeat(2, 1fr)' : '1fr')};
  gap: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.nearBlack};
    margin-bottom: 0.5rem;
  }

  input, textarea, select {
    width: 100%;
    padding: 0.75rem 0.9rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.white};
    font-family: inherit;
    font-size: 0.95rem;
    color: ${({ theme }) => theme.colors.nearBlack};
    transition: ${({ theme }) => theme.transitions.base};
  }

  textarea { min-height: 120px; resize: vertical; }

  input:focus, textarea:focus, select:focus {
    border-color: ${({ theme }) => theme.colors.gold};
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.goldSoft};
  }
`;

export const PrimaryBtn = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${({ theme }) => theme.colors.nearBlack};
  color: #fff;
  font-weight: 600;
  font-size: 0.8rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => theme.colors.nearBlack};
  transition: ${({ theme }) => theme.transitions.base};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.gold};
    border-color: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.nearBlack};
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const SaveBtn = styled(PrimaryBtn)``;

export const SecondaryBtn = styled(PrimaryBtn)`
  background: transparent;
  color: ${({ theme }) => theme.colors.nearBlack};
  border-color: ${({ theme }) => theme.colors.borderStrong};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.gold};
    border-color: ${({ theme }) => theme.colors.gold};
  }
`;

export const DangerBtn = styled(PrimaryBtn)`
  background: transparent;
  color: ${({ theme }) => theme.colors.danger};
  border-color: ${({ theme }) => theme.colors.danger};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.danger};
    color: #fff;
  }
`;

export const Row = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

export const Toast = styled.div`
  padding: 0.65rem 0.9rem;
  font-size: 0.88rem;
  line-height: 1.3;
  border-left: 3px solid ${({ theme, $error }) => ($error ? theme.colors.danger : theme.colors.gold)};
  background: ${({ theme, $error }) => ($error ? '#FDECEA' : theme.colors.goldSoft)};
  color: ${({ theme, $error }) => ($error ? theme.colors.danger : theme.colors.goldDark)};
  display: inline-flex;
  align-items: center;
`;

export const LoginWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.nearBlack};
  padding: 2rem 1rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at top left, rgba(200,169,81,0.15), transparent 50%),
      radial-gradient(ellipse at bottom right, rgba(200,169,81,0.08), transparent 50%);
  }
`;

export const LoginCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;
  background: ${({ theme }) => theme.colors.white};
  padding: 3rem 2.5rem;
  border-top: 3px solid ${({ theme }) => theme.colors.gold};
  box-shadow: ${({ theme }) => theme.shadows.feature};

  .eyebrow {
    display: block;
    text-align: center;
    font-size: 0.72rem;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.goldDark};
    margin-bottom: 0.5rem;
  }

  h2 {
    text-align: center;
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1.8rem;
    color: ${({ theme }) => theme.colors.nearBlack};
    margin-bottom: 0.35rem;
  }

  > p {
    text-align: center;
    color: ${({ theme }) => theme.colors.textMuted};
    margin-bottom: 1.75rem;
    font-size: 0.92rem;
  }
`;

export const LoginError = styled.div`
  padding: 0.8rem 1rem;
  background: #FDECEA;
  color: ${({ theme }) => theme.colors.danger};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  margin-bottom: 1rem;
  font-size: 0.88rem;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;

  th, td {
    padding: 0.85rem 1rem;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    font-size: 0.92rem;
  }

  th {
    background: ${({ theme }) => theme.colors.offWhite};
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.16em;
    color: ${({ theme }) => theme.colors.nearBlack};
  }

  tr:hover td { background: ${({ theme }) => theme.colors.offWhite}; }
`;

export const SectionCard = styled.div`
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.offWhite};
  margin-bottom: 1rem;
  position: relative;

  h3 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1.05rem;
    color: ${({ theme }) => theme.colors.nearBlack};
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

export const ItemPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.75rem;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.85rem;
  margin: 0.25rem 0.25rem 0.25rem 0;

  button {
    color: ${({ theme }) => theme.colors.danger};
    font-size: 0.75rem;
  }
`;

export const ImageStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

export const ImageThumb = styled.div`
  position: relative;
  width: 110px;
  height: 80px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border: 1px solid ${({ theme }) => theme.colors.border};
  }

  button {
    position: absolute;
    top: -6px; right: -6px;
    width: 22px; height: 22px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.nearBlack};
    color: ${({ theme }) => theme.colors.gold};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
  }
`;
